import type { Candle, Timeframe } from "../types";
import { HistoryLoader } from "./HistoryLoader";
import { getMarketData } from "./MarketDataEngine";
import { TickAggregator } from "./TickAggregator";
import { getReplay } from "../replay/ReplayEngine";

type Listener = (candles: Candle[], loading: boolean) => void;

// A live candle series for symbol × timeframe, fed by the MarketDataEngine tick bus.
export class CandleSeries {
  private agg: TickAggregator;
  private listeners = new Set<Listener>();
  private unsub: (() => void) | null = null;
  private alive = true;
  loading = true;

  constructor(readonly symbol: string, readonly tf: Timeframe) {
    this.agg = new TickAggregator(tf);
    this.start();
  }

  get candles(): Candle[] { return this.agg.candles; }

  private emit() {
    const snapshot = this.agg.candles.slice();
    this.listeners.forEach((l) => l(snapshot, this.loading));
  }

  private start() {
    const md = getMarketData();
    // Server candles are only used LIVE; in replay every timeframe is built from replayed ticks (no look-ahead).
    if (this.tf.kind === "candle" && !getReplay().isReplaying(this.symbol)) {
      HistoryLoader.candles(this.symbol, this.tf.seconds!, 5000).then((cands) => {
        if (!this.alive) return;
        this.agg.seed(cands);
        this.loading = !cands.length;
        this.emit();
        this.unsub = md.subscribe(this.symbol, (ticks) => {
          for (const t of ticks) this.agg.push(t);
          this.emit();
        });
      }).catch(() => {});
      return;
    }
    this.unsub = md.subscribe(this.symbol, (ticks, initial) => {
      if (initial) this.agg.reset(ticks);
      else for (const t of ticks) this.agg.push(t);
      if (this.agg.candles.length) this.loading = false;
      this.emit();
    }, 5000);
  }

  onChange(cb: Listener): () => void {
    this.listeners.add(cb);
    cb(this.agg.candles.slice(), this.loading);
    return () => this.listeners.delete(cb);
  }

  destroy() {
    this.alive = false;
    this.unsub?.();
    this.listeners.clear();
  }
}
